// CAT Mock 5 — VARC section.
const catMock5Varc = [
  {
    id: 1,
    type: "mcq",
    passageId: "rc1",
    passage: `"When a measure becomes a target, it ceases to be a good measure." The maxim, usually attributed to the economist Charles Goodhart, describes a pattern that recurs wherever institutions try to manage what they cannot directly observe. A hospital cannot easily measure the quality of its care, so it measures waiting times; a school cannot directly observe learning, so it measures examination results. As long as no one is rewarded for the number itself, it may track the underlying quality reasonably well. Once careers and budgets depend on it, people find ways to move the number that do not move the quality: patients are kept waiting in ambulances so that the emergency department's clock does not start, and teaching narrows to the questions likely to appear on the test.

It is tempting to conclude that the solution is to abandon targets altogether and trust professionals to do their jobs. But this overlooks why measures were introduced in the first place. Before waiting times were published, some patients waited for many hours, and nobody outside the hospital knew. Professional judgement had its own blind spots, and the measure exposed them. The more defensible lesson is narrower. A single indicator, however well chosen, invites gaming because it gives a precise answer to the question of what counts. A basket of indicators, rotated unpredictably and combined with inspection by people who understand the work, is harder to game, though also more expensive and less transparent. What cannot be had is a measure that is at once simple, public, consequential and incorruptible; institutions must decide which of these properties they are willing to give up.`,
    question: "The examples of patients kept in ambulances and of teaching narrowed to likely test questions serve to illustrate:",
    options: [
      "A. the failure of professionals to understand the purpose of measurement.",
      "B. ways of improving a measured figure without improving the quality it is meant to reflect.",
      "C. the impossibility of measuring the quality of hospitals and schools.",
      "D. the benefits of publishing performance figures."
    ],
    answer: "B"
  },
  {
    id: 2,
    type: "mcq",
    passageId: "rc1",
    question: "The author's attitude towards the proposal to abandon targets altogether is best described as:",
    options: [
      "A. enthusiastic, because targets inevitably corrupt institutions.",
      "B. indifferent, because both approaches have equal costs.",
      "C. dismissive of professional judgement in every form.",
      "D. sceptical, because measures were introduced partly to expose failures that professional judgement had missed."
    ],
    answer: "D"
  },
  {
    id: 3,
    type: "mcq",
    passageId: "rc1",
    question: "According to the passage, a basket of indicators rotated unpredictably and combined with expert inspection:",
    options: [
      "A. is harder to game, but at the cost of greater expense and reduced transparency.",
      "B. eliminates gaming entirely.",
      "C. is simpler and more public than a single indicator.",
      "D. was the approach used before waiting times were published."
    ],
    answer: "A"
  },
  {
    id: 4,
    type: "mcq",
    passageId: "rc1",
    question: "Which of the following would the author most likely agree with?",
    options: [
      "A. Any measure can be made incorruptible if it is chosen carefully enough.",
      "B. Publishing performance figures always does more harm than good.",
      "C. Designing a system of measurement involves trading desirable properties off against one another.",
      "D. Professionals should never be held to account by external measures."
    ],
    answer: "C"
  },
  {
    id: 5,
    type: "mcq",
    passageId: "rc2",
    passage: `Few images are as familiar as the world map that hangs in classrooms, and few are so often accused of distortion. On the projection devised by Gerardus Mercator in 1569, Greenland appears roughly the size of Africa, though Africa is some fourteen times larger. Critics have read political meaning into this, arguing that the map flatters the temperate north at the expense of the tropics, where most formerly colonised nations lie. In the 1970s a rival projection, promoted by Arno Peters, preserved relative areas and was adopted by several aid agencies and churches as a more equitable picture of the world.

The controversy, however, often misstates what Mercator was trying to do. His projection was designed for navigation: it is the only one on which a line of constant compass bearing appears as a straight line, so that a sailor could lay a ruler between two ports and read off the course to steer. The inflation of high latitudes is not an oversight but the unavoidable price of that property, since no flat map can preserve both the shapes and the areas of a curved surface. Every projection distorts something; the question is only what. The Peters map, for its part, keeps areas true by stretching tropical continents into elongated shapes that many cartographers found misleading in their own way.

The real problem, then, was not Mercator's choice but its migration from the chart table to the classroom wall, where a map built to answer one question was taken as an answer to all of them. Cartographers today generally recommend compromise projections for general reference, which distort both shape and area a little rather than either a great deal.`,
    question: "The passage suggests that the inflated size of Greenland on Mercator's map is best understood as:",
    options: [
      "A. a deliberate attempt to exaggerate the importance of northern countries.",
      "B. a necessary consequence of designing the map so that lines of constant compass bearing appear straight.",
      "C. an error that was corrected in later editions of the map.",
      "D. the result of inaccurate surveys of the far north."
    ],
    answer: "B"
  },
  {
    id: 6,
    type: "mcq",
    passageId: "rc2",
    question: "The author's main criticism is directed at:",
    options: [
      "A. Mercator, for ignoring the relative areas of continents.",
      "B. Peters, for promoting his projection on political grounds.",
      "C. the use of a map designed for navigation as a general-purpose picture of the world.",
      "D. aid agencies, for adopting the Peters projection."
    ],
    answer: "C"
  },
  {
    id: 7,
    type: "mcq",
    passageId: "rc2",
    question: "Which of the following best describes the author's view of the Peters projection?",
    options: [
      "A. It preserved areas at the cost of distorting shapes, which some found misleading.",
      "B. It solved the problem of distortion that Mercator had created.",
      "C. Like Mercator's, it was designed mainly for navigation.",
      "D. It was rejected by every organisation that considered it."
    ],
    answer: "A"
  },
  {
    id: 8,
    type: "mcq",
    passageId: "rc2",
    question: "Which of the following, if true, would most weaken the critics' political reading of Mercator's map described in the first paragraph?",
    options: [
      "A. Greenland is somewhat larger than most people assume.",
      "B. Several aid agencies still use the Peters projection.",
      "C. Most classrooms have now replaced Mercator's map.",
      "D. The projection enlarges regions near the South Pole exactly as much as those equally near the North Pole."
    ],
    answer: "D"
  },
  {
    id: 9,
    type: "mcq",
    passageId: "rc3",
    passage: `The idea that the Earth is immensely old did not arrive as a single discovery. In the late eighteenth century the Scottish naturalist James Hutton, studying an outcrop where nearly vertical layers of rock were capped by horizontal ones, reasoned that the lower layers must have been laid down, tilted and worn flat before the upper ones settled on top — a sequence requiring spans of time beyond any human chronicle. He concluded that he could find "no vestige of a beginning, no prospect of an end". Yet Hutton offered no number. The first serious attempts to put a figure on the Earth's age came a century later from physics rather than geology, and they came out strikingly low. Lord Kelvin, calculating how long a once-molten planet would take to cool to its present temperature, arrived at somewhere between twenty and a hundred million years, and later narrowed his estimate towards the lower end.

Geologists and biologists were uneasy — Darwin among them, since natural selection seemed to need far longer — but Kelvin's mathematics was difficult to fault. The flaw lay in a premise no one at the time could have checked. Kelvin had assumed that the Earth had no internal source of heat. The discovery of radioactivity at the end of the century revealed one, and radioactive decay also supplied a clock: by measuring the proportions of certain elements and their decay products in a rock, its age could be calculated. Within a few decades the estimates had grown to billions of years. The episode is sometimes told as a story of physics arrogantly overruling the evidence of the rocks. It is better told as a reminder that a calculation is only as sound as its least examined assumption.`,
    question: "Hutton's inference about the age of the Earth rested on:",
    options: [
      "A. calculations of the rate at which the planet had cooled.",
      "B. historical records of earlier geological changes.",
      "C. the realisation that the arrangement of the rock layers implied a lengthy sequence of deposition, tilting and erosion.",
      "D. the discovery of fossils in the lower layers of the outcrop."
    ],
    answer: "C"
  },
  {
    id: 10,
    type: "mcq",
    passageId: "rc3",
    question: "According to the passage, Kelvin's estimate was mistaken chiefly because:",
    options: [
      "A. he assumed, as could not then be checked, that the Earth had no internal source of heat.",
      "B. he made errors in his mathematics.",
      "C. he deliberately ignored geological evidence.",
      "D. he measured the Earth's present temperature incorrectly."
    ],
    answer: "A"
  },
  {
    id: 11,
    type: "mcq",
    passageId: "rc3",
    question: "The author's view of the way the episode is \"sometimes told\" is that this telling:",
    options: [
      "A. accurately portrays physicists as dismissive of geological evidence.",
      "B. exaggerates the role Darwin played in the debate.",
      "C. underestimates the importance of Hutton's work.",
      "D. misplaces the lesson, which concerns the danger of an unexamined assumption rather than one discipline's arrogance."
    ],
    answer: "D"
  },
  {
    id: 12,
    type: "mcq",
    passageId: "rc3",
    question: "The discovery of radioactivity is presented in the passage as significant because it:",
    options: [
      "A. showed that the Earth was cooling faster than Kelvin had thought.",
      "B. both undermined a premise of Kelvin's calculation and supplied a method for dating rocks.",
      "C. confirmed Hutton's claim that the Earth has no beginning.",
      "D. reconciled Kelvin's estimate with Darwin's requirements."
    ],
    answer: "B"
  },
  {
    id: 13,
    type: "mcq",
    passageId: "rc4",
    passage: `Lists of "untranslatable" words circulate widely: the Portuguese saudade, the Danish hygge, the German Schadenfreude. Each is offered as proof that some languages can express feelings that others cannot, and sometimes as evidence that speakers of different languages experience the world differently. The claim is half true at best. That English had no single word for Schadenfreude did not prevent English speakers from recognising the feeling — they borrowed the word precisely because they knew what it meant once it was explained. A word that can be explained in a sentence has, in an important sense, been translated, if not compressed.

What is genuinely lost in translation is usually less conspicuous than a missing word. It is the web of associations a word carries: the rhythm of a line of verse, the register that marks a phrase as formal or intimate, the echo of a proverb or a hymn that a native reader catches without noticing. A translator can render the meaning of a sentence and still lose its tone, or preserve the tone by departing from the literal meaning. This is why translators of poetry disagree so sharply, and why a single poem can sustain dozens of translations, none of which supersedes the others.

The fascination with single untranslatable words may itself be a kind of tourism. It offers the pleasure of difference in a portable form, a curiosity to collect, while sparing us the harder recognition that every sentence in a foreign language resists us a little, not only the exotic ones.`,
    question: "The author regards the claim that certain words are \"untranslatable\" as:",
    options: [
      "A. only partly true, because a word that can be explained in a sentence has in effect been translated.",
      "B. entirely false, because every word has an exact equivalent in other languages.",
      "C. entirely true, because speakers of different languages experience the world differently.",
      "D. impossible to evaluate without studying each language in depth."
    ],
    answer: "A"
  },
  {
    id: 14,
    type: "mcq",
    passageId: "rc4",
    question: "The example of Schadenfreude is used to show that:",
    options: [
      "A. English speakers did not experience the emotion until they borrowed the word.",
      "B. German has a richer emotional vocabulary than English.",
      "C. borrowed words lose much of their original meaning.",
      "D. the absence of a single equivalent word does not prevent speakers from understanding a feeling."
    ],
    answer: "D"
  },
  {
    id: 15,
    type: "mcq",
    passageId: "rc4",
    question: "According to the passage, what is most often genuinely lost in translation?",
    options: [
      "A. The literal meaning of individual words.",
      "B. The grammatical structure of the original sentence.",
      "C. The associations, rhythm and register that words and phrases carry.",
      "D. The names of particular emotions."
    ],
    answer: "C"
  },
  {
    id: 16,
    type: "mcq",
    passageId: "rc4",
    question: "The author calls the fascination with untranslatable words \"a kind of tourism\" because it:",
    options: [
      "A. is popular mainly among people who travel abroad.",
      "B. offers the pleasure of difference in a convenient form while evading the broader difficulty of translation.",
      "C. encourages people to learn foreign languages properly.",
      "D. treats foreign languages as more valuable than one's own."
    ],
    answer: "B"
  },
  {
    id: 17,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. But the new crops came with a hidden cost.\n2. In the 1960s, high-yielding varieties of wheat transformed agriculture across much of Asia.\n3. Harvests doubled within a decade, and famine, once a recurring threat, became rare.\n4. The varieties needed far more water and fertiliser than traditional ones, and groundwater levels began to fall.",
    answer: "2314"
  },
  {
    id: 18,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. Comb-like fringes on the leading edge of each wing break up the turbulence that would otherwise produce noise.\n2. The answer, it turned out, lay in their feathers.\n3. Owls are famous for flying almost silently.\n4. Researchers long wondered how a bird of that size could move through the air without a sound.",
    answer: "3421"
  },
  {
    id: 19,
    type: "tita",
    question: "The five sentences (labelled 1, 2, 3, 4, 5) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the five numbers as your answer.\n1. The first postage stamps, introduced in Britain in 1840, changed this by making the sender pay in advance at a flat rate.\n2. Before then, letters were usually paid for by the recipient, and the charge depended on the distance and the number of sheets.\n3. Recipients sometimes refused letters to avoid the fee, having already gleaned the message from codes marked on the outside.\n4. The reform was an immediate success, and the volume of mail more than doubled within a year.\n5. Within a few decades, nearly every country had adopted adhesive stamps of its own.",
    answer: "23145"
  },
  {
    id: 20,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nStudies of learning have long focused on what happens while we study. A growing body of research suggests that what happens afterwards matters as much. During sleep, the brain appears to replay recent experiences, strengthening some memories and discarding others. People who sleep after learning a task typically perform it better the next day than those who stay awake for the same length of time, even when both groups have had the same amount of practice.",
    options: [
      "A. Research suggests that sleep after learning helps consolidate memories, so the period following study matters as well as the study itself.",
      "B. Sleep is more important than study for learning a new task.",
      "C. People should always study immediately before going to sleep.",
      "D. The brain discards most of what it learns during sleep."
    ],
    answer: "A"
  },
  {
    id: 21,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nTipping is often defended as a way for customers to reward good service. Yet studies of restaurant tips find that the size of a tip is only weakly related to the quality of service as rated by customers themselves; it depends more on factors such as the size of the bill, the weather and the server's appearance. If tips mainly reflect such factors, they function less as a reward for service than as a variable part of wages that shifts the risk of a bad night from the employer to the worker.",
    options: [
      "A. Tipping should be abolished because it is unfair to customers.",
      "B. Customers tip more generously when the weather is good.",
      "C. A server's appearance is the main determinant of their income.",
      "D. Because tips track service quality only weakly, they act less as rewards than as an unstable part of wages that transfers risk to workers."
    ],
    answer: "D"
  },
  {
    id: 22,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nBusiness folklore holds that the first company into a new market enjoys a lasting advantage. The evidence is mixed. Pioneers do sometimes establish brands and standards that later entrants struggle to dislodge, but they also bear the costs of educating customers and of making mistakes that followers can learn from cheaply. Many celebrated market leaders were not the first in their field but the first to get the product right.",
    options: [
      "A. Being first into a market usually leads to failure.",
      "B. Being first can bring advantages, but pioneers also bear costs that followers avoid, and success often goes to whoever first gets the product right.",
      "C. Followers always outperform the pioneers of a market.",
      "D. A strong brand is the most important asset a company can have."
    ],
    answer: "B"
  },
  {
    id: 23,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Honeybees tell their hive-mates where food is to be found by performing a \"waggle dance\".\n2. The angle of the dance relative to the vertical indicates the direction of the food relative to the sun.\n3. Honey has been found in ancient Egyptian tombs, still edible after thousands of years.\n4. The duration of the waggling portion of the dance signals how far away the food is.\n5. Remarkably, bees adjust the angle through the day to allow for the sun's movement across the sky.",
    answer: "3"
  },
  {
    id: 24,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Early railways ran on \"local time\", with each town setting its clocks by the sun.\n2. As timetables spread, the confusion of dozens of slightly different local times became intolerable.\n3. Railway companies therefore adopted a single standard time across their networks.\n4. Governments later made standard time official, eventually dividing the world into time zones.\n5. Steam locomotives were gradually replaced by diesel and electric engines in the twentieth century.",
    answer: "5"
  }
];

export default catMock5Varc;
